import React from 'react';
import { Rect, Ellipse, Line, Text, Group, Arc, Circle } from 'react-konva';
import {
  getEntityLabel, getShapeFill, getShapeStroke, getWallPoints, WALL_STROKE_WIDTH,
} from './constants';

/**
 * Renders a single layout shape on the Konva stage.
 */
const FloorPlanShape = ({
  shape,
  colorByOccupancy = false,
  highlighted = false,
  invalid = false,
  selected = false,
  listening = true,
  onSelect,
  onDragEnd,
  onTransformEnd,
  onWallEndpointDrag,
  draggable = false,
}) => {
  const fill = getShapeFill(shape, { colorByOccupancy });
  const stroke = invalid ? '#dc2626' : getShapeStroke(shape, { colorByOccupancy, highlighted: highlighted || selected });
  const baseStroke = shape.elementType === 'wall'
    ? WALL_STROKE_WIDTH
    : (shape.style?.strokeWidth || 2);
  const strokeWidth = invalid
    ? Math.max(baseStroke + 2, 3)
    : selected || highlighted
      ? Math.max(baseStroke + (shape.elementType === 'wall' ? 0 : 1), baseStroke)
      : baseStroke;

  const common = {
    id: shape.id,
    name: shape.id,
    x: shape.x || 0,
    y: shape.y || 0,
    rotation: shape.rotation || 0,
    opacity: shape.style?.opacity ?? 1,
    draggable: draggable && !shape.locked && shape.elementType !== 'wall',
    listening,
    onClick: (e) => {
      e.cancelBubble = true;
      onSelect?.(shape, e);
    },
    onTap: (e) => {
      e.cancelBubble = true;
      onSelect?.(shape, e);
    },
    onDragEnd: (e) => onDragEnd?.(shape, e),
    onTransformEnd: (e) => onTransformEnd?.(shape, e),
  };

  const label = getEntityLabel(shape);
  const showLabel = ['office', 'coworking', 'seat', 'meeting_room', 'stairs', 'area'].includes(shape.elementType);
  const w = shape.width || 80;
  const h = shape.height || 60;

  if (shape.shapeType === 'ellipse' || shape.elementType === 'seat') {
    const sw = shape.width || 28;
    const sh = shape.height || 28;
    return (
      <Group {...common}>
        <Ellipse
          x={sw / 2}
          y={sh / 2}
          radiusX={sw / 2}
          radiusY={sh / 2}
          fill={fill}
          stroke={stroke}
          strokeWidth={strokeWidth}
        />
        {showLabel && label && (
          <Text
            text={label}
            width={sw}
            height={sh}
            align="center"
            verticalAlign="middle"
            fontSize={Math.min(10, sw / 3)}
            fill="#111827"
            listening={false}
          />
        )}
      </Group>
    );
  }

  if (shape.elementType === 'wall' || shape.shapeType === 'line') {
    const pts = getWallPoints(shape);
    return (
      <Group
        id={shape.id}
        name={shape.id}
        x={shape.x || 0}
        y={shape.y || 0}
        rotation={shape.rotation || 0}
        draggable={draggable && !shape.locked}
        listening={listening}
        onClick={(e) => { e.cancelBubble = true; onSelect?.(shape, e); }}
        onTap={(e) => { e.cancelBubble = true; onSelect?.(shape, e); }}
        onDragEnd={(e) => onDragEnd?.(shape, e)}
      >
        {/* Hit area for easier selection */}
        <Line
          points={pts}
          stroke="transparent"
          strokeWidth={Math.max(WALL_STROKE_WIDTH + 10, 14)}
          lineCap="round"
        />
        <Line
          points={pts}
          stroke={stroke}
          strokeWidth={WALL_STROKE_WIDTH}
          lineCap="round"
          lineJoin="round"
          listening={false}
        />
        {selected && draggable && !shape.locked && (
          <>
            {[0, 1].map((endpointIndex) => {
              const px = pts[endpointIndex * 2];
              const py = pts[endpointIndex * 2 + 1];
              return (
                <Circle
                  key={endpointIndex}
                  x={px}
                  y={py}
                  radius={7}
                  fill="#fff"
                  stroke="#f97316"
                  strokeWidth={2}
                  draggable
                  onDragMove={(e) => {
                    e.cancelBubble = true;
                    const node = e.target;
                    onWallEndpointDrag?.(shape, endpointIndex, { x: node.x(), y: node.y() }, { live: true });
                  }}
                  onDragEnd={(e) => {
                    e.cancelBubble = true;
                    const node = e.target;
                    onWallEndpointDrag?.(shape, endpointIndex, { x: node.x(), y: node.y() }, { live: false });
                  }}
                  onMouseDown={(e) => { e.cancelBubble = true; }}
                  onTouchStart={(e) => { e.cancelBubble = true; }}
                />
              );
            })}
          </>
        )}
      </Group>
    );
  }

  // Architectural plan-view door: hinge + leaf + swing arc
  if (shape.elementType === 'door') {
    const radius = Math.max(w, 24);
    return (
      <Group {...common}>
        {/* Swing area */}
        <Arc
          x={0}
          y={0}
          innerRadius={0}
          outerRadius={radius}
          angle={90}
          rotation={-90}
          fill={fill}
          stroke={stroke}
          strokeWidth={1}
          dash={[4, 3]}
          opacity={0.85}
        />
        {/* Door leaf (open) */}
        <Line
          points={[0, 0, 0, -radius]}
          stroke={stroke}
          strokeWidth={3}
          lineCap="round"
          listening={false}
        />
        {/* Hinge / jamb mark */}
        <Circle x={0} y={0} radius={3} fill={stroke} listening={false} />
        {/* Opening threshold */}
        <Line
          points={[0, 0, radius, 0]}
          stroke={stroke}
          strokeWidth={1}
          dash={[2, 2]}
          listening={false}
        />
      </Group>
    );
  }

  if (shape.shapeType === 'polygon' && shape.points?.length >= 6) {
    return (
      <Line
        {...common}
        points={shape.points}
        closed
        fill={fill}
        stroke={stroke}
        strokeWidth={strokeWidth}
      />
    );
  }

  if (shape.elementType === 'stairs') {
    return (
      <Group {...common}>
        <Rect width={w} height={h} fill={fill} stroke={stroke} strokeWidth={strokeWidth} cornerRadius={2} />
        {[0.25, 0.5, 0.75].map((t) => (
          <Line
            key={t}
            points={[4, h * t, w - 4, h * t]}
            stroke={stroke}
            strokeWidth={1}
            listening={false}
          />
        ))}
        <Text
          text={label || 'Stairs'}
          width={w}
          height={h}
          align="center"
          verticalAlign="middle"
          fontSize={Math.min(12, Math.max(8, w / 8))}
          fill="#111827"
          listening={false}
        />
      </Group>
    );
  }

  if (shape.elementType === 'area') {
    return (
      <Group {...common}>
        <Rect
          width={w}
          height={h}
          fill={fill}
          stroke={stroke}
          strokeWidth={strokeWidth}
          dash={[6, 4]}
          cornerRadius={4}
        />
        <Text
          text={label || 'Area'}
          width={w}
          height={h}
          align="center"
          verticalAlign="middle"
          fontSize={Math.min(14, Math.max(10, w / 7))}
          fill="#134e4a"
          fontStyle="bold"
          listening={false}
        />
      </Group>
    );
  }

  return (
    <Group {...common}>
      <Rect
        width={w}
        height={h}
        fill={fill}
        stroke={stroke}
        strokeWidth={strokeWidth}
        cornerRadius={2}
      />
      {showLabel && label && (
        <Text
          text={label}
          width={w}
          height={h}
          align="center"
          verticalAlign="middle"
          fontSize={Math.min(12, Math.max(8, w / 8))}
          fill="#111827"
          padding={4}
          listening={false}
        />
      )}
    </Group>
  );
};

export default FloorPlanShape;
